import {
  AnalyticsSourceMiddlewareCallback,
  AnalyticsSourceMiddlewareEvent,
  captureAnalyticsSessionParams,
  getAnalyticsSessionAdsParams,
  getAnalyticsSessionCampaignParams,
} from '@psl/functions/analytics/index.shared';
import { config } from '@psl/functions/config';
import type { UserTraits } from '@segment/analytics-react-native';
import { Statsig } from 'statsig-react-native-expo';
import { AnalyticEvents } from '../../generated/AnalyticEvents';
import { makeDebug } from '../../utils/makeDebug';
import { isDebugEnabled } from '../debugging';

const debug = makeDebug('app:analytics', isDebugEnabled('analytics'));

const analytics = {
  track(msg: string, extraData?: any, options?: any) {
    void window.analytics?.track?.(msg, extraData, options);
  },
  screen: (_page: string, _params: any) => {
    // void window.analytics?.page(page, params);
  },
};

declare global {
  interface Window {
    // when run in storybook mode, this method does not exist
    analytics?: {
      load: (key: string) => void;
      page: (screen: string, params: any) => void;
      identify?: (uid: string, traits?: UserTraits, options?: any) => void;
      track?: (...args: any[]) => void;
      debug: (value: boolean) => void;
      on: (
        event: string,
        callback: (event: string, properties: any, options: any) => void,
      ) => void;
      reset: () => void;
      addSourceMiddleware: (
        middleware: (args: {
          payload: any;
          integrations: Record<string, any>;
          next: (payload: any) => void;
        }) => void,
      ) => void;
    };
  }
}

const sourceMiddlewareCallbacks: AnalyticsSourceMiddlewareCallback[] = [];

export const addAnalyticsSourceMiddlewareEventListener = (
  callback: AnalyticsSourceMiddlewareCallback,
) => {
  const handler = (e: AnalyticsSourceMiddlewareEvent) => {
    callback(e);
  };
  sourceMiddlewareCallbacks.push(handler);
  return () => {
    const index = sourceMiddlewareCallbacks.indexOf(handler);
    if (index !== -1) {
      sourceMiddlewareCallbacks.splice(index, 1);
    }
  };
};

export function setupAnalytics() {
  captureAnalyticsSessionParams();

  window.analytics?.load(config.segmentWriteKey);

  window.analytics?.debug(isDebugEnabled('analytics'));

  window.analytics?.addSourceMiddleware(args => {
    const { payload, next } = args;
    const event: AnalyticsSourceMiddlewareEvent = { payload };
    for (const callback of sourceMiddlewareCallbacks) {
      callback(event);
    }
    if (!event.defaultPrevented) {
      next(payload);
    } else {
      debug('event prevented:', event);
      return null;
    }
  });

  AnalyticEvents.default.addEventListener((event, properties = {}) => {
    const adsParams = getAnalyticsSessionAdsParams();

    const propertiesWithStableID: Record<string, any> = {
      ...properties,
      ...('element_action' in properties
        ? {
            // remove element_action if it's the same as the event name
            element_action:
              properties?.element_action === event
                ? undefined
                : properties.element_action,
          }
        : undefined),
      statsigCustomIDs: ['stableID', Statsig.getStableID()],
    };

    if (Object.keys(adsParams).length) {
      propertiesWithStableID.ads_params = adsParams;
    }

    if (__DEV__) {
      debug(
        `analytics:event -> ${event}, properties: %O`,
        propertiesWithStableID ?? '(none)',
      );
    }

    analytics.track(event, propertiesWithStableID, {
      campaign: getAnalyticsSessionCampaignParams({ stripePrefix: true }),
    });

    // @ts-ignore
    if (window.DEBUG_TRACKING) {
      console.log(
        `[EVENT]: ${event} - ${JSON.stringify(propertiesWithStableID)}`,
      );
    }
  });
}

export function setAnalyticsUser(uid?: string, traits?: UserTraits) {
  if (uid != null) {
    void window.analytics?.identify?.(uid, traits, {
      campaign: {
        ...getAnalyticsSessionCampaignParams({ stripePrefix: true }),
        ...getAnalyticsSessionAdsParams(),
      },
    });
  }
}

export function resetAnalyticsUser() {
  void window.analytics?.reset();
}
